import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ChatCenteredDots, Lightbulb, Bug, Sparkle, Heart, WarningCircle, Plus, ThumbsUp, CheckCircle, Hourglass } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  suggestion:      { label: 'Suggestion', icon: Lightbulb, color: 'text-amber-400' },
  bug:             { label: 'Bug',         icon: Bug,       color: 'text-red-400'   },
  feature_request: { label: 'Feature',     icon: Sparkle,   color: 'text-blue-400'  },
  complaint:       { label: 'Complaint',   icon: WarningCircle, color: 'text-orange-400' },
  praise:          { label: 'Praise',      icon: Heart,     color: 'text-blue-400'  },
  other:           { label: 'Other',       icon: ChatCenteredDots, color: 'text-slate-400' },
};

const STATUS_META = {
  open:        { label: 'Open',        color: 'bg-blue-500/20 text-blue-400' },
  planned:     { label: 'Planned',     color: 'bg-indigo-500/20 text-indigo-400' },
  in_progress: { label: 'In progress', color: 'bg-amber-500/20 text-amber-400' },
  shipped:     { label: 'Shipped',     color: 'bg-green-500/20 text-green-400' },
  wont_do:     { label: "Won't do",    color: 'bg-slate-700 text-slate-400' },
  duplicate:   { label: 'Duplicate',   color: 'bg-slate-700 text-slate-400' },
};

const FeedbackPage = () => {
  const [tab, setTab] = useState('public');     // 'public' | 'mine'
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ category: 'suggestion', subject: '', body: '' });

  const load = async () => {
    setLoading(true);
    try {
      const url = tab === 'mine' ? '/api/feedback/mine' : '/api/feedback/public';
      const res = await axios.get(`${API}${url}`);
      setItems(res.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const submit = async () => {
    if (!form.subject.trim() || !form.body.trim()) {
      toast.error('Subject and message are required'); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/feedback`, form);
      toast.success("Thanks — we read every submission.");
      setOpen(false);
      setForm({ category: 'suggestion', subject: '', body: '' });
      setTab('mine');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed');
    } finally { setSaving(false); }
  };

  const upvote = async (id) => {
    try {
      const res = await axios.post(`${API}/api/feedback/${id}/upvote`);
      setItems(prev => prev.map(i => i.id === id ? { ...i, upvotes: res.data.upvotes, has_upvoted: res.data.has_upvoted } : i));
    } catch {}
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6" data-testid="feedback-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white flex items-center gap-2">
            <ChatCenteredDots weight="fill" className="w-7 h-7 text-blue-400" />
            Feedback & Requests
          </h1>
          <p className="text-slate-400 text-sm mt-1">Tell us what's broken, what you wish existed, what you love.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="px-4 py-2 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 flex items-center gap-2"
              data-testid="new-feedback-btn">
              <Plus className="w-5 h-5" /> Submit
            </button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle>Submit Feedback</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-3">
              <div>
                <Label>Type</Label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                  className="w-full bg-slate-800 border border-white/10 rounded-md px-3 py-2 text-white"
                  data-testid="feedback-category">
                  {Object.entries(CATEGORY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <Label>Subject</Label>
                <Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
                  maxLength={140} className="bg-slate-800 border-white/10"
                  data-testid="feedback-subject" />
              </div>
              <div>
                <Label>Details</Label>
                <Textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })}
                  rows={6} maxLength={5000} className="bg-slate-800 border-white/10"
                  data-testid="feedback-body"
                  placeholder="What's on your mind?" />
              </div>
              <button onClick={submit} disabled={saving}
                className="w-full py-3 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 disabled:opacity-50"
                data-testid="submit-feedback-btn">
                {saving ? 'Sending…' : 'Send'}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-1 bg-slate-900/50 rounded-xl p-1 w-fit mb-4">
        <button onClick={() => setTab('public')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === 'public' ? 'bg-blue-500/20 text-blue-400' : 'text-slate-500 hover:text-white'}`}
          data-testid="feedback-tab-public">
          Public Roadmap
        </button>
        <button onClick={() => setTab('mine')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === 'mine' ? 'bg-blue-500/20 text-blue-400' : 'text-slate-500 hover:text-white'}`}
          data-testid="feedback-tab-mine">
          My Submissions
        </button>
      </div>

      {loading ? (
        <p className="text-slate-500 text-sm">Loading…</p>
      ) : items.length === 0 ? (
        <div className="glass-light rounded-2xl p-12 text-center">
          <ChatCenteredDots className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500">
            {tab === 'public' ? 'No public items yet — your submission could be the first.' : "You haven't submitted anything yet."}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(it => {
            const cat = CATEGORY_META[it.category] || CATEGORY_META.other;
            const status = STATUS_META[it.status] || STATUS_META.open;
            const Icon = cat.icon;
            return (
              <motion.div key={it.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className="glass-light rounded-xl p-4" data-testid={`feedback-${it.id}`}>
                <div className="flex items-start gap-3">
                  {tab === 'public' && (
                    <button onClick={() => upvote(it.id)}
                      className={`flex flex-col items-center gap-0.5 px-3 py-2 rounded-lg flex-shrink-0 ${
                        it.has_upvoted ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                      }`}
                      data-testid={`upvote-${it.id}`}>
                      <ThumbsUp weight={it.has_upvoted ? 'fill' : 'regular'} className="w-4 h-4" />
                      <span className="text-xs font-bold">{it.upvotes}</span>
                    </button>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Icon weight="fill" className={`w-4 h-4 ${cat.color}`} />
                      <span className="text-xs uppercase tracking-wider font-bold text-slate-500">{cat.label}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${status.color}`}>{status.label}</span>
                    </div>
                    <p className="text-white font-semibold">{it.subject}</p>
                    <p className="text-sm text-slate-400 mt-1 whitespace-pre-line">{it.body}</p>
                    {it.admin_response && (
                      <div className="mt-3 p-3 bg-blue-500/5 border-l-2 border-blue-500/50 rounded">
                        <p className="text-xs uppercase tracking-wider font-bold text-blue-400 mb-1">LustLink team</p>
                        <p className="text-sm text-slate-200 whitespace-pre-line">{it.admin_response}</p>
                      </div>
                    )}
                    <p className="text-xs text-slate-600 mt-2">
                      {tab === 'public' ? `by ${it.user_name} · ` : ''}
                      {new Date(it.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FeedbackPage;
